import type {
  AccountExport,
  Brand,
  BrandDetail,
  BrowsingHistoryView,
  Category,
  CategoryNode,
  Channel,
  ConsentStatus,
  CreateOutboundClickRequest,
  CreateOutboundClickResponse,
  Favorite,
  FavoriteView,
  FlashSaleView,
  HomeBanner,
  HomeFeature,
  LegalDocument,
  PriceAlertView,
  ProductDetail,
  ProductListItem,
  ProductSearchQuery,
  UpsertFavoriteRequest,
  User,
} from "@glow-price/shared";

import { apiRequest } from "./client";

export type ProductSearchResult = {
  items: ProductListItem[];
  total: number;
  page: number;
  pageSize: number;
};

export const auth = {
  login: (input: { code?: string; mockOpenid?: string; nickname?: string; avatarUrl?: string }) =>
    apiRequest<{ token: string; user: User }>("/api/auth/login", { method: "POST", body: input }),
};

export const products = {
  search: (query: ProductSearchQuery) =>
    apiRequest<ProductSearchResult>("/api/products/search", {
      query: query as Record<string, string | number | boolean | undefined>,
    }),
  detail: (id: string) => apiRequest<ProductDetail>(`/api/products/${id}`),
  recentDrops: (params: { limit?: number; windowDays?: number; minDropPercent?: number } = {}) =>
    apiRequest<{ items: ProductListItem[] }>("/api/products/recent-drops", { query: params }),
};

export const channels = {
  list: () => apiRequest<{ items: Channel[] }>("/api/channels"),
  brands: () => apiRequest<{ items: Brand[] }>("/api/brands"),
};

export const brandsApi = {
  detail: (id: string) => apiRequest<BrandDetail>(`/api/brands/${id}`),
};

export const categories = {
  list: () => apiRequest<{ items: Category[]; tree: CategoryNode[] }>("/api/categories"),
};

export const favorites = {
  list: () => apiRequest<{ items: FavoriteView[] }>("/api/favorites"),
  upsert: (body: UpsertFavoriteRequest) =>
    apiRequest<Favorite>("/api/favorites", { method: "POST", body }),
  update: (id: string, body: { targetPrice?: number | null; notifyEnabled?: boolean }) =>
    apiRequest<Favorite>(`/api/favorites/${id}`, { method: "PATCH", body }),
  remove: (id: string) =>
    apiRequest<{ id: string }>(`/api/favorites/${id}`, { method: "DELETE" }),
};

export const outbound = {
  create: (body: CreateOutboundClickRequest) =>
    apiRequest<CreateOutboundClickResponse>("/api/outbound-clicks", { method: "POST", body }),
  updateStatus: (clickId: string, body: { status: "success" | "failed"; failureReason?: string }) =>
    apiRequest<unknown>(`/api/outbound-clicks/${clickId}/status`, { method: "PATCH", body }),
};

export const browsingHistory = {
  list: (limit = 50) =>
    apiRequest<{ items: BrowsingHistoryView[] }>("/api/browsing-history", {
      query: { limit },
    }),
};

export const flashSales = {
  list: (params: { phase?: "active" | "upcoming" | "ended"; limit?: number } = {}) =>
    apiRequest<{ items: FlashSaleView[] }>("/api/flash-sales", { query: params }),
};

export const notifications = {
  list: () => apiRequest<{ items: PriceAlertView[] }>("/api/notifications"),
  unreadCount: () => apiRequest<{ count: number }>("/api/notifications/unread-count"),
  dismiss: (id: string) =>
    apiRequest<{ id: string }>(`/api/notifications/${id}/dismiss`, { method: "PATCH" }),
};

export const homeSettings = {
  feature: () => apiRequest<HomeFeature>("/api/home/feature"),
  banners: () => apiRequest<{ items: HomeBanner[] }>("/api/home/banners"),
};

export const legalDocs = {
  privacy: () => apiRequest<LegalDocument>("/api/legal/privacy"),
  terms: () => apiRequest<LegalDocument>("/api/legal/terms"),
};

export const me = {
  getConsent: () => apiRequest<ConsentStatus | null>("/api/me/consent"),
  saveConsent: (body: { privacyVersion: string; termsVersion: string }) =>
    apiRequest<ConsentStatus>("/api/me/consent", { method: "POST", body }),
  export: () => apiRequest<AccountExport>("/api/me/export"),
  scheduleDelete: () =>
    apiRequest<{ scheduledAt: string }>("/api/me/account/delete", { method: "POST" }),
  cancelDelete: () =>
    apiRequest<unknown>("/api/me/account/delete/cancel", { method: "POST" }),
};
